import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { run, useLoad } from "../lib/util";
import { Alert, Badge, Btn, Field, Input, ProductDialog, ProductSelect, SectionTitle, Table, Empty } from "../components/kit";
import { useSchool } from "./ctx";

function SubDialog({ initial, onClose, onSaved }) {
  const { products } = useSchool();
  const [f, setF] = useState({ from_product_id: null, to_product_id: null, preparation: "", month: "", reason: "", restrictions_note: "", confirmed: false, ...initial });
  const a = products.find((p) => p.id === f.from_product_id);
  const b = products.find((p) => p.id === f.to_product_id);
  const save = async () => {
    const body = { ...f, preparation: f.preparation || null, month: f.month || null, reason: f.reason || null, restrictions_note: f.restrictions_note || null, version: f.version ?? null };
    ["id", "school_id", "created_at", "updated_at"].forEach((k) => delete body[k]);
    if (await run(() => (f.id ? api.put(`/s/substitutions/${f.id}`, body) : api.post("/s/substitutions", body)), "Substituição salva")) { onSaved(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="sub-dialog">
        <DialogHeader><DialogTitle>Equivalência / substituição</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Previsto no cardápio"><ProductSelect testid="sub-from-select" products={products} value={f.from_product_id} onChange={(id) => setF({ ...f, from_product_id: id })} /></Field>
          <Field label="Produto comprado / disponível"><ProductSelect testid="sub-to-select" products={products} value={f.to_product_id} onChange={(id) => setF({ ...f, to_product_id: id })} /></Field>
          {a && b && (a.group || "") !== (b.group || "") && <Alert>Os produtos estão em grupos alimentares diferentes ({a.group || "sem grupo"} × {b.group || "sem grupo"}). Confira antes de confirmar.</Alert>}
          <Field label="Somente na preparação (vazio = todas)"><Input data-testid="sub-prep-input" value={f.preparation || ""} onChange={(e) => setF({ ...f, preparation: e.target.value })} /></Field>
          <Field label="Somente no mês (vazio = todos)"><Input type="month" value={f.month || ""} onChange={(e) => setF({ ...f, month: e.target.value })} /></Field>
          <Field label="Motivo"><Input data-testid="sub-reason-input" value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
          <Field label="Restrições alimentares verificadas"><Input value={f.restrictions_note || ""} placeholder="ex.: nenhum aluno com restrição a frango" onChange={(e) => setF({ ...f, restrictions_note: e.target.value })} /></Field>
          <label className="flex gap-2 text-sm"><input type="checkbox" data-testid="sub-confirm-checkbox" checked={f.confirmed} onChange={(e) => setF({ ...f, confirmed: e.target.checked })} />Confirmo esta substituição (somente substituições confirmadas são usadas na distribuição)</label>
        </div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="sub-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { products, reloadProducts, tick, bump } = useSchool();
  const [subs, reloadSubs] = useLoad(async () => (await api.get("/s/substitutions")).data, [tick]);
  const [edit, setEdit] = useState(null);
  const [sub, setSub] = useState(null);
  const pname = (id) => products.find((p) => p.id === id)?.name || "?";
  const delP = async (p) => window.confirm(`Remover ${p.name}?`) && (await run(() => api.delete(`/s/products/${p.id}`), "Removido")) && bump();
  const delS = async (s) => (await run(() => api.delete(`/s/substitutions/${s.id}`), "Removida")) && (reloadSubs(), bump());
  return (
    <div className="space-y-6">
      <SectionTitle title="Produtos e correlação com o cardápio" subtitle="Gêneros do estoque, unidades e equivalências confirmadas pela escola. Nenhuma substituição é feita automaticamente.">
        <Btn data-testid="new-product-button" onClick={() => setEdit({})}><Plus className="w-4 h-4" />Novo produto</Btn>
        <Btn variant="navy" data-testid="new-sub-button" onClick={() => setSub({})}><Plus className="w-4 h-4" />Nova substituição</Btn>
      </SectionTitle>
      <Table testid="products-table">
        <thead><tr><th>Produto</th><th>Unidade de estoque</th><th>Grupo alimentar</th><th>Tipo</th><th /></tr></thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.id}>
              <td className="font-semibold">{p.name}</td><td>{p.unit}</td><td>{p.group}</td>
              <td>{p.ready_made ? <Badge tone="info">Comprado pronto</Badge> : ""}</td>
              <td className="whitespace-nowrap">
                <Btn variant="ghost" onClick={() => setEdit(p)}><Pencil className="w-4 h-4" /></Btn>
                <Btn variant="ghost" onClick={() => delP(p)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {!products.length && <Empty>Nenhum produto. Eles também podem ser criados durante a conferência dos documentos.</Empty>}
      <div>
        <h3 className="font-heading font-bold text-lg text-[#0B192C] mb-2">Substituições e equivalências</h3>
        <p className="text-xs text-slate-500 mb-3">Variedades distintas (ex.: feijão carioca e feijão-fradinho) são produtos diferentes e só se substituem com confirmação.</p>
        <Table testid="subs-table">
          <thead><tr><th>Previsto</th><th>Utilizar</th><th>Preparação</th><th>Mês</th><th>Motivo / restrições</th><th>Situação</th><th /></tr></thead>
          <tbody>
            {(subs || []).map((s) => (
              <tr key={s.id}>
                <td>{pname(s.from_product_id)}</td><td className="font-semibold">{pname(s.to_product_id)}</td>
                <td>{s.preparation || "todas"}</td><td>{s.month || "todos"}</td>
                <td className="text-xs">{s.reason}<div className="text-slate-400">{s.restrictions_note}</div></td>
                <td><Badge tone={s.confirmed ? "green" : "amber"}>{s.confirmed ? "Confirmada" : "Aguardando confirmação"}</Badge></td>
                <td className="whitespace-nowrap">
                  <Btn variant="ghost" onClick={() => setSub(s)}><Pencil className="w-4 h-4" /></Btn>
                  <Btn variant="ghost" onClick={() => delS(s)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {edit && <ProductDialog initial={edit.id ? edit : undefined} onClose={() => setEdit(null)} onSaved={() => { reloadProducts(); bump(); }} />}
      {sub && <SubDialog initial={sub} onClose={() => setSub(null)} onSaved={() => { reloadSubs(); bump(); }} />}
    </div>
  );
}
